import goldBadge from "../../../../assets/Certificate/badge.png";
import Signature from "../../../../assets/Certificate/pysignature.png";
import QR from "../../../../assets/Certificate/qr_with_border.png";
import Bookmark from "../../../../assets/Certificate/watermark.png";
import logo from "../../../../assets/logo.png";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useRef, useState } from "react";

interface CertificateGenProps {
  name: string;
  fontSize: string;
  coursename: string;
  duration: string;
  completionmonth: string;
  completionyear: string;
  uid: string;
  issueDate: string;
  isSaved: boolean; // Add the isSaved prop
}

const CertificatePythonGen = ({
  name,
  fontSize,
  coursename,
  duration,
  completionmonth,
  completionyear,
  uid,
  issueDate,
  isSaved, // Destructure the isSaved prop
}: CertificateGenProps) => {
  const certificateRef = useRef<HTMLDivElement>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [fromEmail, setFromEmail] = useState("contact@gbdrift.com");
  const [toEmail, setToEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Function to validate email
  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.com$/;
    if (!email) {
      return "Email is required.";
    }
    if (!regex.test(email)) {
      return "Email must contain '@' and end with '.com'.";
    }
    if (email.length > 50) {
      return "Email is too long.";
    }
    return ""; // No error
  };

  const handleToEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setToEmail(value);
    setEmailError(validateEmail(value));
  };

  const generatePDF = () => {
    if (certificateRef.current) {
      // Capture the certificate content using html2canvas
      html2canvas(certificateRef.current, {
        useCORS: true,
        logging: true,
        scrollX: 0,
        scrollY: -window.scrollY,
        x: 0,
        y: 0,
        width: certificateRef.current.offsetWidth,
        height: certificateRef.current.offsetHeight,
        backgroundColor: null,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");

        const doc = new jsPDF();
        doc.addImage(imgData, "PNG", 0, 0, 210, 297);
        doc.save("Certificate.pdf");
      });
    }
  };

  // Function to send email
  const sendEmail = async () => {
    const validationError = validateEmail(toEmail);
    if (validationError) {
      setEmailError(validationError);
      return;
    }

    if (!fromEmail || !toEmail) {
      alert("Please fill in both email fields.");
      return;
    }

    setIsSending(true);

    try {
      // Capture the certificate as an image
      const canvas = await html2canvas(certificateRef.current!, {
        useCORS: true,
        logging: true,
        scrollX: 0,
        scrollY: -window.scrollY,
        width: certificateRef.current!.offsetWidth,
        height: certificateRef.current!.offsetHeight,
        backgroundColor: null,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
      });

      pdf.addImage(imgData, "PNG", 0, 0, 210, 297);

      // Convert the PDF to Base64
      const pdfBase64 = pdf.output("datauristring").split(",")[1]; // Extract Base64 content

      const response = await axios.post(
        "https://api.brevo.com/v3/smtp/email",
        {
          sender: {
            name: "Great Masters AI",
            email: fromEmail,
          },
          to: [{ email: toEmail }],
          subject: "Python Training Certificate",
          htmlContent: `
            <p>Hi ${name},</p>
            <p><span style="background-color: #007bff; color: white; padding: 1px 5px; border-radius: 2px;">Congratulations</span> 
            on successfully completing the Python Training. Please find the E-copy of your certificate attached below.</p>
            <p>We trust that the knowledge and skills acquired in this course will benefit you in current and future work roles. We thank you for choosing to study with Great Masters AI (Developers Guidance Program).</p>
            <p>I highly encourage you to post this certificate on LinkedIn to showcase your expertise in Python to everyone.</p>
            <p>Happy learning!!!</p>
            <p><strong>Thanks and regards,<br>Great Masters AI</strong></p>
          `,
          attachment: [
            {
              content: pdfBase64, // Attach as PDF
              name: `${name}_Python (Certificate).pdf`,
            },
          ],
        },
        {
          headers: {
            "api-key": import.meta.env.VITE_BREVO_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 201) {
        alert("Email sent successfully!");
        setShowEmailModal(false);
        window.location.reload();
      }
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Failed to send email. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <div className="flex justify-center items-center h-min-screen my-10">
        <div
          ref={certificateRef}
          className="w-4/5 max-w-[900px] border-8 border-[#e2caee] p-8 bg-white text-center relative font-sans"
          style={{
            backgroundImage: `url(${Bookmark})`,
            backgroundPosition: "center",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
          }}
        >
          {/* Header Section */}
          <div className=" relative z-10">
            <img
              src={logo}
              alt="Great Masters AI Logo"
              className="w-96 mx-auto mb-10 pt-3"
            />
            <img
              src={goldBadge}
              alt="Gold Badge"
              className="w-44 absolute top-[-60px]"
            />
            <br />
            <h1 className="text-5xl font-extrabold text-gray-800 uppercase mb-3 tracking-wider">
              Certificate
            </h1>
            <h3 className="text-2xl font-semibold text-gray-500 mb-2">
              of Recognition
            </h3>
            <p className="text-xl text-gray-700">This is to certify that</p>
          </div>

          {/* Name Section */}
          <div className="my-8 relative z-10">
            {/* Use the name prop here */}
            <h2 className={`${fontSize} font-bold text-gray-900 mb-4`}>
              {name}
            </h2>
            <p className="text-lg text-gray-700 leading-8">
              Has successfully completed {duration} months of intensive training
              in <br />
              <strong className="font-semibold text-3xl text-gray-900">
                {coursename}
              </strong>{" "}
              <br />
              conducted by Great Masters AI in {completionmonth} {completionyear}
            </p>
          </div>

          {/* Footer Section */}
          <div className="grid grid-cols-2 gap-8 mt-10">
            <div className="text-left">
              <img src={QR} alt="Gold Badge" className="w-48 mb-2" />
              <p className="text-sm font-medium text-gray-800">
                Scan This to Verify Credentials
              </p>
              <p className="text-sm font-medium text-gray-700 mt-8">
                Validation Code:
              </p>
              <p className="text-lg font-semibold text-gray-900">{uid}</p>
            </div>

            <div className="text-right ml-auto">
              <img src={Signature} alt="QR Code" className="w-40 h-40" />
              <p className="text-xl font-semibold italic mb-1">Dinesh Kumar</p>
              <span className="text-lg text-gray-600 pr-4">Great Masters AI</span>
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700">Issue Date:</p>
                <p className="text-lg font-semibold text-gray-900">
                  {issueDate}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conditionally render the buttons based on isSaved state */}
      {isSaved && (
      <div className="flex justify-center mt-4 space-x-4">
        <button
          onClick={generatePDF}
          className="px-6 py-3 bg-[#28761e] text-white text-lg font-semibold rounded-lg hover:bg-[#e65c00] focus:outline-none focus:ring-2 focus:ring-[#e2caee]"
        >
          Download Certificate as PDF
        </button>

        <button
          onClick={() => setShowEmailModal(true)}
          className="px-6 py-3 bg-[#800000] text-white text-lg font-semibold rounded-lg hover:bg-[#6495ED] focus:outline-none focus:ring-2 focus:ring-[#e2caee]"
        >
          Send to Mail
        </button>
      </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">
              Send "Python" Certificate via Email
            </h2>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="fromEmail"
                  className="block text-sm font-medium text-gray-700"
                >
                  From Email
                </label>
                <input
                  type="email"
                  id="fromEmail"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300"
                  readOnly
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="toEmail"
                  className="block text-sm font-medium text-gray-700"
                >
                  To Email
                </label>
                <input
                  type="email"
                  id="toEmail"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300"
                  value={toEmail}
                  onChange={handleToEmailChange}
                />
                {emailError && (
                  <p className="text-red-500 text-sm mt-1">{emailError}</p>
                )}
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowEmailModal(false)}
                className="mr-4 px-4 py-2 bg-gray-500 text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={sendEmail}
                disabled={isSending}
                className="px-4 py-2 bg-[#e2caee] text-white rounded-lg"
              >
                {isSending ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CertificatePythonGen;
